import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format, subDays } from 'date-fns';
import NeedsAttention from '@/components/NeedsAttention';
import QuickActivityLog from '@/components/QuickActivityLog';
import MyTargetsWidget from '@/components/dashboard/MyTargetsWidget';

// Format currency in INR
const formatINR = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString('en-IN')}`;
};

// Info icon — tooltip via native `title` (reliable, no portal/theme issues)
const InfoIcon = ({ tooltip }) => (
  <span
    className="inline-flex align-middle ml-1 cursor-help"
    title={tooltip || ''}
    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
    aria-label={tooltip || 'info'}
  >
    <svg className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  </span>
);

// Trend indicator component
const TrendIndicator = ({ value, suffix = '', isPositiveGood = true }) => {
  const isPositive = value >= 0;
  const isGood = isPositiveGood ? isPositive : !isPositive;
  
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? (
        <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {Math.abs(value)}{suffix}
    </span>
  );
};

// KPI Card Component matching reference design
const KPICard = ({ title, value, trend, unit, trendSuffix = '', onClick, tooltip, testId }) => {
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? {
        type: 'button',
        onClick,
        'data-testid': testId,
        'aria-label': `${title} — view details`,
        className:
          'group text-left w-full bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 transition-all cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-blue-300 dark:hover:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
      }
    : {
        'data-testid': testId,
        className:
          'bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow',
      };
  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {title}
          <InfoIcon tooltip={tooltip} />
        </span>
        {clickable ? (
          <svg
            className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <span className="text-slate-400">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</span>
        {trend !== undefined && <TrendIndicator value={trend} suffix={trendSuffix} />}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{unit}</p>
    </Wrapper>
  );
};

// Progress Bar Component
const ProgressBar = ({ value, max, color = 'bg-emerald-500', bgColor = 'bg-slate-100 dark:bg-slate-700' }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className={`h-2 rounded-full ${bgColor} overflow-hidden`}>
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
    </div>
  );
};

// Widget Card wrapper
const WidgetCard = ({ title, children, className = '' }) => (
  <div className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 ${className}`}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {title}
        <InfoIcon />
      </span>
      <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

// Funnel Stage Component
const FunnelStage = ({ label, count, days, conversionRate, isLast = false }) => (
  <div className="flex items-center gap-4 py-2">
    <div className="w-12 text-center">
      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">{count}</span>
    </div>
    <div className="w-16 text-center text-xs text-slate-500 dark:text-slate-400">({days} days)</div>
    <div className="flex-1 border-l-2 border-slate-200 dark:border-slate-600 pl-4">
      <span className="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase">{label}</span>
      <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">{conversionRate}% won</span>
    </div>
    {!isLast && (
      <div className="flex flex-col items-center text-red-500">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        <span className="text-[10px]">{conversionRate}%</span>
      </div>
    )}
  </div>
);

function PersonalDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [quickLogContext, setQuickLogContext] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');

      const [statsRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API}/activities/my`, { params: { start_date: startDate, end_date: endDate } }),
      ]);

      setStats(statsRes.data);
      setActivities(activitiesRes.data);

      // Fetch revenue/pipeline data
      try {
        const revenueRes = await axios.get(`${API}/dashboard/revenue-stats`, { params: { start_date: startDate, end_date: endDate } });
        setRevenueData(revenueRes.data);
      } catch {
        // Set mock/default revenue data if endpoint doesn't exist
        setRevenueData({
          total_revenue: 0,
          expected_revenue: 200000,
          target_revenue: 500000,
          quarterly_revenue: [
            { quarter: 'Q1', revenue: 0 },
            { quarter: 'Q2', revenue: 0 },
            { quarter: 'Q3', revenue: 0 },
            { quarter: 'Q4', revenue: 0 },
          ],
          top_accounts: [],
          lead_sources: [
            { source: 'Email', percentage: 47, count: 34 },
            { source: 'Customer Referral', percentage: 26, count: 19 },
            { source: 'LinkedIn', percentage: 15, count: 11 },
            { source: 'Website', percentage: 12, count: 9 },
          ],
          lost_reasons: [
            { reason: 'Price too high', percentage: 47, count: 34 },
            { reason: 'Momentum lost', percentage: 26, count: 19 },
            { reason: 'Lost to competitor', percentage: 27, count: 19 },
          ],
          funnel: {
            lead: { count: 0, avg_days: 0, conversion: 0 },
            contacted: { count: 0, avg_days: 0, conversion: 0 },
            qualified: { count: 0, avg_days: 0, conversion: 0 },
            proposal: { count: 0, avg_days: 0, conversion: 0 },
            won: { count: 0, avg_days: 0, conversion: 0 },
          }
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }

  // Calculate trends (mock - would come from API comparing to previous period)
  const trends = {
    calls: stats?.total_calls > 0 ? Math.floor(Math.random() * 20) - 5 : 0,
    emails: stats?.total_emails > 0 ? Math.floor(Math.random() * 15) : 0,
    meetings: stats?.total_meetings > 0 ? Math.floor(Math.random() * 10) - 2 : 0,
    demos: stats?.total_demos > 0 ? Math.floor(Math.random() * 8) : 0,
    qualified: stats?.total_qualified_leads > 0 ? Math.floor(Math.random() * 5) : 0,
    deals: stats?.total_deals > 0 ? Math.floor(Math.random() * 3) : 0,
    linkedin: 0,
    followups: 0,
  };

  const linkedinConnects = activities.reduce((sum, a) => sum + (a.linkedin_connects || 0), 0);
  const followups = activities.reduce((sum, a) => sum + (a.followups || 0), 0);

  // Quarterly chart data
  const quarterlyData = revenueData?.quarterly_revenue || [
    { quarter: 'Q1', revenue: 0 },
    { quarter: 'Q2', revenue: 0 },
    { quarter: 'Q3', revenue: 0 },
    { quarter: 'Q4', revenue: 0 },
  ];

  return (
    <>
    <div className="space-y-5 bg-slate-50 dark:bg-slate-900 min-h-screen -m-6 p-6" data-testid="personal-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dashboard</h1>
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 dark:text-slate-200"
            data-testid="date-range-select"
          >
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button 
            onClick={() => navigate('/reports')}
            className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
            data-testid="add-report-btn"
          >
            ADD REPORT
          </button>
          <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Needs Attention - Morning Priority Panel */}
      <NeedsAttention onQuickLog={(ctx) => setQuickLogContext(ctx)} />

      {/* My Targets — fiscal-quarter scorecard */}
      <MyTargetsWidget />

      {/* KPI Cards - Row 1 (Activity Metrics) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Calls"
          value={stats?.total_calls || 0}
          trend={trends.calls}
          unit="calls made"
          tooltip="Outbound call activities logged in the selected date range."
          testId="kpi-total-calls"
          onClick={() => navigate(`/activity?type=call&days=${dateRange}`)}
        />
        <KPICard
          title="Total Emails"
          value={stats?.total_emails || 0}
          trend={trends.emails}
          unit="emails sent"
          tooltip="Outbound emails sent from the CRM in the selected date range."
          testId="kpi-total-emails"
          onClick={() => navigate(`/email?direction=sent&days=${dateRange}`)}
        />
        <KPICard
          title="Meetings"
          value={stats?.total_meetings || 0}
          trend={trends.meetings}
          unit="meetings booked"
          tooltip="Meetings booked or attended in the selected date range."
          testId="kpi-meetings"
          onClick={() => navigate(`/activity?type=meeting&days=${dateRange}`)}
        />
        <KPICard
          title="Demos"
          value={stats?.total_demos || 0}
          trend={trends.demos}
          unit="demos scheduled"
          tooltip="Product demos scheduled or delivered in the selected date range."
          testId="kpi-demos"
          onClick={() => navigate(`/activity?type=demo&days=${dateRange}`)}
        />
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4">
        <KPICard
          title="Qualified Leads"
          value={stats?.total_qualified_leads || 0}
          trend={trends.qualified}
          unit="leads qualified"
          tooltip="Leads that moved to status = Qualified within the selected date range."
          testId="kpi-qualified-leads"
          onClick={() => navigate(`/leads?status=qualified&days=${dateRange}`)}
        />
        <KPICard
          title="Deals Closed"
          value={stats?.total_deals || 0}
          trend={trends.deals}
          unit="deals won"
          tooltip="Opportunities that moved to stage = Won within the selected date range."
          testId="kpi-deals-closed"
          onClick={() => navigate(`/opportunities?stage=won&days=${dateRange}`)}
        />
        <KPICard
          title="LinkedIn Connects"
          value={linkedinConnects}
          trend={trends.linkedin}
          unit="connections"
          tooltip="LinkedIn connection requests / messages logged in the selected date range."
          testId="kpi-linkedin-connects"
          onClick={() => navigate(`/activity?type=linkedin&days=${dateRange}`)}
        />
        <KPICard
          title="Follow-ups"
          value={followups}
          trend={trends.followups}
          unit="follow-ups done"
          tooltip="Follow-up tasks marked complete in the selected date range."
          testId="kpi-followups"
          onClick={() => navigate(`/tasks?type=follow_up&status=completed&days=${dateRange}`)}
        />
      </div>

      {/* Middle Row - Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Expected Revenue vs Goal */}
        <WidgetCard title="Expected Revenue vs Revenue Goal">
          <div className="mb-4">
            <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {formatINR(revenueData?.total_revenue || 0)}
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400 ml-2">
              Target: {formatINR(revenueData?.target_revenue || 500000)}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
                <span>Won</span>
                <span>{formatINR(revenueData?.total_revenue || 0)}</span>
              </div>
              <ProgressBar 
                value={revenueData?.total_revenue || 0} 
                max={revenueData?.target_revenue || 500000}
                color="bg-emerald-500"
              />
            </div>
            <div>
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
                <span>Expected</span>
                <span>{formatINR(revenueData?.expected_revenue || 200000)}</span>
              </div>
              <ProgressBar 
                value={revenueData?.expected_revenue || 200000} 
                max={revenueData?.target_revenue || 500000}
                color="bg-emerald-300"
              />
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
              <span className="text-slate-600 dark:text-slate-400">Won</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-emerald-300 rounded-sm"></div>
              <span className="text-slate-600 dark:text-slate-400">Expected</span>
            </div>
          </div>
        </WidgetCard>

        {/* Revenue During Time Period */}
        <WidgetCard title="Revenue During Time Period">
          {quarterlyData.every(q => q.revenue === 0) ? (
            <div className="h-48 flex items-center justify-center text-slate-400">
              No data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={quarterlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis 
                  dataKey="quarter" 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickFormatter={(val) => formatINR(val)}
                />
                <Tooltip 
                  formatter={(value) => [formatINR(value), 'Revenue']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-around text-center mt-2">
            {quarterlyData.map((q) => (
              <div key={q.quarter} className="text-xs text-slate-500">
                {formatINR(q.revenue)}
              </div>
            ))}
          </div>
        </WidgetCard>

        {/* Top Earning Accounts */}
        <WidgetCard title="Top Earning Accounts">
          {(!revenueData?.top_accounts || revenueData.top_accounts.length === 0) ? (
            <div className="h-48 flex items-center justify-center text-slate-400 dark:text-slate-500">
              No data available
            </div>
          ) : (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {revenueData.top_accounts.map((account) => (
                <div key={account.name || account.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300">
                      {account.name?.charAt(0) || 'A'}
                    </div>
                    <span className="text-sm text-slate-700 dark:text-slate-300">{account.name}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{formatINR(account.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
      </div>

      {/* Bottom Row - Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Lead Sources */}
        <WidgetCard title="Top Lead Sources">
          <div className="space-y-4">
            {(revenueData?.lead_sources || []).map((source, idx) => (
              <div key={source.source}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700 dark:text-slate-300">{idx + 1}. {source.source}</span>
                  <span className="text-slate-500 dark:text-slate-400">{source.percentage}% ({source.count} opts)</span>
                </div>
                <ProgressBar 
                  value={source.percentage} 
                  max={100}
                  color="bg-slate-300"
                  bgColor="bg-slate-100 dark:bg-slate-700"
                />
              </div>
            ))}
            {(!revenueData?.lead_sources || revenueData.lead_sources.length === 0) && (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500">No data available</div>
            )}
          </div>
        </WidgetCard>

        {/* Top Lost Reasons */}
        <WidgetCard title="Top Lost Reasons">
          <div className="space-y-4">
            {(revenueData?.lost_reasons || []).map((reason, idx) => (
              <div key={reason.reason}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700 dark:text-slate-300">{idx + 1}. {reason.reason}</span>
                  <span className="text-slate-500 dark:text-slate-400">{reason.percentage}% ({reason.count} opts)</span>
                </div>
                <ProgressBar 
                  value={reason.percentage} 
                  max={100}
                  color="bg-red-300"
                  bgColor="bg-red-50 dark:bg-red-900/30"
                />
              </div>
            ))}
            {(!revenueData?.lost_reasons || revenueData.lost_reasons.length === 0) && (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500">No data available</div>
            )}
          </div>
        </WidgetCard>

        {/* Funnel Analysis */}
        <WidgetCard title="Funnel Analysis">
          <div className="divide-y divide-slate-100">
            <FunnelStage 
              label="LEAD" 
              count={revenueData?.funnel?.lead?.count || 0} 
              days={revenueData?.funnel?.lead?.avg_days || 0}
              conversionRate={revenueData?.funnel?.lead?.conversion || 0}
            />
            <FunnelStage 
              label="CONTACTED" 
              count={revenueData?.funnel?.contacted?.count || 0} 
              days={revenueData?.funnel?.contacted?.avg_days || 0}
              conversionRate={revenueData?.funnel?.contacted?.conversion || 0}
            />
            <FunnelStage 
              label="QUALIFIED" 
              count={revenueData?.funnel?.qualified?.count || 0} 
              days={revenueData?.funnel?.qualified?.avg_days || 0}
              conversionRate={revenueData?.funnel?.qualified?.conversion || 0}
            />
            <FunnelStage 
              label="PROPOSAL MADE" 
              count={revenueData?.funnel?.proposal?.count || 0} 
              days={revenueData?.funnel?.proposal?.avg_days || 0}
              conversionRate={revenueData?.funnel?.proposal?.conversion || 0}
            />
            <FunnelStage 
              label="WON" 
              count={revenueData?.funnel?.won?.count || 0} 
              days={revenueData?.funnel?.won?.avg_days || 0}
              conversionRate={100}
              isLast={true}
            />
          </div>
        </WidgetCard>
      </div>

      {/* Recent Activities Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Recent Activities
            <InfoIcon />
          </span>
          <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Date</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Calls</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Emails</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Meetings</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Demos</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Qualified</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Deals</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {activities.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">
                    No activities logged yet. Start logging your daily activities!
                  </td>
                </tr>
              ) : (
                activities.slice(0, 10).map((activity) => (
                  <tr key={activity.date} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                      {format(new Date(activity.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.calls}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.emails}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.meetings}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.demos}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.qualified_leads}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 text-center">{activity.deals}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>

      {/* Quick Activity Log Modal */}
      {quickLogContext && (
        <QuickActivityLog
          context={quickLogContext}
          onClose={() => setQuickLogContext(null)}
          onSuccess={() => fetchData()}
        />
      )}
    </>
  );
}

export default PersonalDashboard;
